/**
 * Shared guarded-retry helper for the auth gate functions in `./auth` that
 * coalesce concurrent callers behind a single in-flight promise:
 * `resolveInitialServerAuthGateState` and `refreshServerAuthGateState`.
 *
 * Both fire the guard for the same reason: a desktop `onStateChange` event
 * can supersede an in-flight promise, clearing the in-flight slot back to
 * null. A caller still awaiting the stale promise, whether it fulfills or
 * rejects, must retry against current state instead of getting handed the
 * answer to a question that no longer applies. forge#3629 covers the
 * fulfilled path; forge#3633 is the rejected path, which needs the identical
 * guard.
 *
 * Extracted out of `./auth`, which is at the ratchet cap in
 * `scripts/check-file-size-ratchet.ts`.
 */
export interface GuardedAuthGateOptions<T> {
  /** Starts the operation whose result this call coalesces. */
  readonly start: () => Promise<T>;
  /** Reads the module-level slot holding the in-flight promise, if any. */
  readonly getSlot: () => Promise<T> | null;
  /** Writes the module-level slot. */
  readonly setSlot: (promise: Promise<T> | null) => void;
  /** Runs once, only when this call's promise is still the current slot. */
  readonly onSettled: (result: T) => void;
  /** Re-enters the outer function to answer against current state. */
  readonly retry: () => Promise<T>;
}

export function startGuardedAuthGate<T>(options: GuardedAuthGateOptions<T>): Promise<T> {
  const guardedPromise: Promise<T> = options
    .start()
    .then(
      (result) => {
        if (options.getSlot() !== guardedPromise) return options.retry();
        options.onSettled(result);
        return result;
      },
      (error: unknown) => {
        if (options.getSlot() !== guardedPromise) return options.retry();
        throw error;
      },
    )
    .finally(() => {
      if (options.getSlot() === guardedPromise) options.setSlot(null);
    });
  options.setSlot(guardedPromise);
  return guardedPromise;
}
